import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  ApiResponse,
  AuthResponse,
  Customer,
  CustomerStatus,
  PagedResult,
  PortalUser,
  SimpleApiResponse,
} from '../models/types';
import { OnboardingData } from '@models/onboarding.types';

@Injectable({
  providedIn: 'root',
})
export class MockApiService {
  // Mock Data - 15 customers for scroll testing
  private mockCustomers: Customer[] = [
    {
      customerId: 'cust-001',
      displayId: '1001',
      name: 'Acme Corp',
      url: 'https://acmecorp.currentware.com',
      domainId: 'acmecorp',
      crmId: 'CRM-123',
      region: 'US-East',
      zone: '153456',
      plan: 'Subscribed',
      status: 'Suspended',
      licensesUsed: 525,
      licensesTotal: 600,
      features: [],
      integrations: [],
      licenses: [
        {
          licenseId: 'lic-001',
          productId: 'BR',
          productName: 'BrowseReporter',
          totalUsers: 600,
          usedUsers: 525,
          varianceAllowed: 10,
          startDate: '2024-01-15',
          endDate: '2025-01-15',
        },
      ],
      createdAt: '2024-01-15',
      adminEmail: 'admin@acmecorp.com',
    },
    {
      customerId: 'cust-002',
      displayId: '1002',
      name: 'Beta Solutions',
      url: 'https://betasolutions.currentware.com',
      domainId: 'betasolutions',
      crmId: 'CRM-456',
      region: 'US-East',
      plan: 'Subscribed',
      status: 'Suspended',
      licensesUsed: 75,
      licensesTotal: 100,
      features: [],
      integrations: [],
      licenses: [
        {
          licenseId: 'lic-002',
          productId: 'AC',
          productName: 'AccessPatrol',
          totalUsers: 100,
          usedUsers: 75,
          varianceAllowed: 5,
          startDate: '2024-02-20',
          endDate: '2025-02-20',
        },
      ],
      createdAt: '2024-02-20',
      adminEmail: 'admin@betasolutions.com',
    },
    {
      customerId: 'cust-003',
      displayId: '1003',
      name: 'Global VFX',
      url: 'https://globalvfx.currentware.com',
      domainId: 'globalvfx',
      crmId: 'CRM-789',
      region: 'US-East',
      zone: '789012',
      plan: 'Subscribed',
      status: 'Active',
      licensesUsed: 750,
      licensesTotal: 800,
      features: [],
      integrations: [],
      licenses: [
        {
          licenseId: 'lic-003',
          productId: 'CS',
          productName: 'CurrentWare Suite',
          totalUsers: 800,
          usedUsers: 750,
          varianceAllowed: 15,
          startDate: '2024-03-10',
          endDate: '2025-03-10',
        },
      ],
      createdAt: '2024-03-10',
      adminEmail: 'admin@globalvfx.com',
    },
    {
      customerId: 'cust-004',
      displayId: '1004',
      name: 'Zeta Consulting',
      url: 'https://zetaconsulting.currentware.com',
      domainId: 'zetaconsulting',
      crmId: 'CRM-012',
      region: 'US-East',
      plan: 'Trial',
      status: 'Trial',
      licensesUsed: 15,
      licensesTotal: 25,
      features: [],
      integrations: [],
      licenses: [
        {
          licenseId: 'lic-004',
          productId: 'BR',
          productName: 'BrowseReporter',
          totalUsers: 25,
          usedUsers: 15,
          varianceAllowed: 3,
          startDate: '2024-06-01',
          endDate: '2024-07-01',
        },
      ],
      createdAt: '2024-06-01',
      adminEmail: 'admin@zetaconsulting.com',
    },
    {
      customerId: 'cust-005',
      displayId: '1005',
      name: 'TechStart Inc',
      url: 'https://techstart.currentware.com',
      domainId: 'techstart',
      crmId: 'CRM-345',
      region: 'US-West',
      zone: '234567',
      plan: 'Subscribed',
      status: 'Active',
      licensesUsed: 45,
      licensesTotal: 50,
      features: [],
      integrations: [],
      licenses: [
        {
          licenseId: 'lic-005',
          productId: 'AC',
          productName: 'AccessPatrol',
          totalUsers: 50,
          usedUsers: 45,
          varianceAllowed: 5,
          startDate: '2024-04-01',
          endDate: '2025-04-01',
        },
      ],
      createdAt: '2024-04-01',
      adminEmail: 'admin@techstart.com',
    },
  ];

  private mockUsers: PortalUser[] = [
    {
      id: 'user-001',
      name: 'System Administrator',
      email: 'admin@currentware.com',
      sub: 'sub-001',
      role: 'Admin',
      isActive: true,
      lastLogin: '2024-12-10T09:30:00Z',
      mfaEnabled: true,
    },
    {
      id: 'user-002',
      name: 'Support Agent',
      email: 'support@currentware.com',
      sub: 'sub-002',
      role: 'Support',
      isActive: true,
      lastLogin: '2024-12-09T14:22:00Z',
      mfaEnabled: false,
    },
    {
      id: 'user-003',
      name: 'Read-Only User',
      email: 'viewer@currentware.com',
      sub: 'sub-003',
      role: 'Viewer',
      isActive: true,
      lastLogin: '2024-12-08T11:00:00Z',
      mfaEnabled: false,
    },
    {
      id: 'user-004',
      name: 'Account Manager',
      email: 'manager@currentware.com',
      sub: 'sub-004',
      role: 'Support',
      isActive: false,
      lastLogin: '2024-11-15T16:00:00Z',
      mfaEnabled: true,
    },
  ];

  // Auth Methods
  /**
   * Mock login - supports 3 user types for testing:
   * - admin@currentware.com / password - Admin role (full access)
   * - support@currentware.com / password - Support role (limited - no notifications/suspend)
   * - viewer@currentware.com / password - Viewer role (view only)
   */
  login(credentials: {
    username: string;
    password: string;
  }): Observable<{ token: string; user: PortalUser }> {
    console.log('[MockApi] Login', credentials);

    // Find user by email (case-insensitive)
    const email = credentials.username.toLowerCase();
    let user: PortalUser;

    switch (email) {
      case 'admin@currentware.com':
        user =
          this.mockUsers.find((u) => u.role === 'Admin') || this.mockUsers[0];
        break;
      case 'support@currentware.com':
        user =
          this.mockUsers.find((u) => u.role === 'Support' && u.isActive) ||
          this.mockUsers[1];
        break;
      case 'viewer@currentware.com':
        user =
          this.mockUsers.find((u) => u.role === 'Viewer') || this.mockUsers[2];
        break;
      default:
        // Default to admin for any other email
        user = this.mockUsers[0];
    }

    return of({
      token: 'mock-jwt-token',
      user: { ...user, lastLogin: new Date().toISOString() },
    }).pipe(delay(500));
  }

  logout(): Observable<any> {
    console.log('[MockApi] Logout');
    return of(true).pipe(delay(200));
  }

  verifyEmail(token: string): Observable<any> {
    console.log('[MockApi] Verify Email', token);
    return of({ success: true, message: 'Email verified' }).pipe(delay(500));
  }

  forgotPassword(email: string): Observable<any> {
    console.log('[MockApi] Forgot Password', email);
    return of({ success: true, message: 'Password reset email sent' }).pipe(
      delay(500)
    );
  }

  resetPassword(data: {
    token: string;
    password: string;
    confirmPassword: string;
  }): Observable<any> {
    console.log('[MockApi] Reset Password', data);
    return of({ success: true, message: 'Password reset successfully' }).pipe(
      delay(500)
    );
  }

  getCurrentUser(): Observable<PortalUser> {
    console.log('[MockApi] Get Current User');
    // Return the first admin user as default
    return of(this.mockUsers[0]).pipe(delay(200));
  }

  /**
   * Get user by username for login verification
   */
  getUserByUsername(username: string): Observable<PortalUser | null> {
    console.log('[MockApi] Get User By Username', username);
    const user = this.mockUsers.find(
      (u) =>
        u.email.toLowerCase().includes(username.toLowerCase()) ||
        u.name.toLowerCase().includes(username.toLowerCase())
    );
    return of(user || null).pipe(delay(200));
  }

  sendOtp(email: string): Observable<SimpleApiResponse> {
    console.log('[MockApi] Send OTP', email);
    return of({ success: true, message: 'OTP sent' }).pipe(delay(500));
  }

  verifyOtp(email: string, code: string): Observable<AuthResponse> {
    console.log('[MockApi] Verify OTP', email, code);
    return of({
      success: true,
      message: 'OTP Verified',
      user: { email, username: email },
    }).pipe(delay(500));
  }

  // Customer Methods
  signUpTrial(customer: OnboardingData): Observable<ApiResponse<Customer>> {
    console.log('[MockApi] Sign Up Trial', customer);
    const newCustomer: Customer = {
      customerId: `cust-${Date.now()}`,
      displayId: `${1000 + this.mockCustomers.length + 1}`,
      name: customer.companyName,
      url: `https://${customer.subdomain}.example.com`,
      crmId: 'CRM-NEW',
      region: customer.phoneCountry,
      plan: 'Trial',
      status: 'Trial',
      licensesUsed: 0,
      licensesTotal: 10,
      features: [],
      integrations: [],
      licenses: [],
      adminUserEmail: customer.email,
      domainId: customer.subdomain,
    };
    return of({
      success: true,
      message: 'Trial created',
      data: newCustomer,
    }).pipe(delay(2000));
  }

  getCustomers(
    page: number = 1,
    pageSize: number = 10,
    query?: string
  ): Observable<PagedResult<Customer>> {
    console.log('[MockApi] Get Customers', { page, pageSize, query });
    let customers = [...this.mockCustomers];
    if (query) {
      customers = customers.filter((c) =>
        c.name.toLowerCase().includes(query.toLowerCase())
      );
    }

    return of({
      success: true,
      message: 'Customers retrieved',
      data: {
        items: customers,
        totalCount: customers.length,
        pageSize,
        pageIndex: page,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      },
    }).pipe(delay(500));
  }

  getCustomer(id: string): Observable<Customer> {
    console.log('[MockApi] Get Customer', id);
    const customer = this.mockCustomers.find((c) => c.customerId === id);
    return of(customer || this.mockCustomers[0]).pipe(delay(200));
  }

  createCustomer(customer: Partial<Customer>): Observable<Customer> {
    console.log('[MockApi] Create Customer', customer);
    const newCustomer = {
      ...this.mockCustomers[0],
      ...customer,
      customerId: `cust-${Date.now()}`,
    } as Customer;
    this.mockCustomers.push(newCustomer);
    return of(newCustomer).pipe(delay(500));
  }

  updateCustomer(
    id: string,
    customer: Partial<Customer>
  ): Observable<Customer> {
    console.log('[MockApi] Update Customer', id, customer);
    const index = this.mockCustomers.findIndex((c) => c.customerId === id);
    if (index !== -1) {
      this.mockCustomers[index] = { ...this.mockCustomers[index], ...customer };
      return of(this.mockCustomers[index]).pipe(delay(500));
    }
    return of(this.mockCustomers[0]).pipe(delay(500));
  }

  deleteCustomer(id: string): Observable<any> {
    console.log('[MockApi] Delete Customer', id);
    this.mockCustomers = this.mockCustomers.filter((c) => c.customerId !== id);
    return of({ success: true }).pipe(delay(500));
  }

  updateCustomerStatus(id: string, status: string): Observable<any> {
    console.log('[MockApi] Update Customer Status', id, status);
    const index = this.mockCustomers.findIndex((c) => c.customerId === id);
    if (index !== -1) {
      this.mockCustomers[index].status = status as CustomerStatus;
    }
    return of({ success: true }).pipe(delay(200));
  }

  resendCustomerWelcomeEmail(customerId: string): Observable<SimpleApiResponse> {
    console.log('[MockApi] Resend Customer Welcome Email', customerId);
    return of({ success: true, message: 'Welcome email sent' }).pipe(delay(300));
  }

  checkSubdomainAvailability(
    subdomain: string
  ): Observable<{ isAvailable: boolean; message: string }> {
    console.log('[MockApi] Check Subdomain', subdomain);
    const taken = ['taken', 'google', 'microsoft'].includes(
      subdomain.toLowerCase()
    );
    return of({
      isAvailable: !taken,
      message: taken ? 'Subdomain taken' : 'Subdomain available',
    }).pipe(delay(300));
  }

  getTenantBySubdomain(subdomain: string): Observable<any> {
    console.log('[MockApi] Get Tenant By Subdomain', subdomain);
    return of({
      tenantId: 'tenant-123',
      name: 'Mock Tenant',
      settings: {},
    }).pipe(delay(200));
  }

  // Admin User Methods
  getUsers(query?: string): Observable<PortalUser[]> {
    console.log('[MockApi] Get Users', query);
    return of(this.mockUsers).pipe(delay(400));
  }

  getUser(id: string): Observable<PortalUser> {
    console.log('[MockApi] Get User', id);
    const user = this.mockUsers.find((u) => u.id === id);
    return of(user || this.mockUsers[0]).pipe(delay(200));
  }

  createUser(user: Partial<PortalUser>): Observable<PortalUser> {
    console.log('[MockApi] Create User', user);
    const newUser = {
      ...this.mockUsers[0],
      ...user,
      id: `user-${Date.now()}`,
    } as PortalUser;
    this.mockUsers.push(newUser);
    return of(newUser).pipe(delay(500));
  }

  updateUser(id: string, user: Partial<PortalUser>): Observable<PortalUser> {
    console.log('[MockApi] Update User', id, user);
    const index = this.mockUsers.findIndex((u) => u.id === id);
    if (index !== -1) {
      this.mockUsers[index] = { ...this.mockUsers[index], ...user };
      return of(this.mockUsers[index]).pipe(delay(500));
    }
    return of(this.mockUsers[0]).pipe(delay(500));
  }

  deleteUser(id: string): Observable<any> {
    console.log('[MockApi] Delete User', id);
    this.mockUsers = this.mockUsers.filter((u) => u.id !== id);
    return of({ success: true }).pipe(delay(500));
  }

  // Logs Methods
  getUserLogs(
    userId: string,
    page: number = 1,
    pageSize: number = 20
  ): Observable<any[]> {
    console.log('[MockApi] Get User Logs', userId);
    return of([
      {
        id: 'log-1',
        timestamp: new Date().toISOString(),
        level: 'INFO',
        message: 'User logged in',
        source: 'Auth',
      },
      {
        id: 'log-2',
        timestamp: new Date().toISOString(),
        level: 'WARN',
        message: 'Failed attempt',
        source: 'Auth',
      },
    ]).pipe(delay(300));
  }

  // Onboarding Methods
  submitOnboarding(data: any): Observable<any> {
    console.log('[MockApi] Submit Onboarding', data);
    return of({ success: true, message: 'Onboarding submitted' }).pipe(
      delay(1000)
    );
  }
}
